import {
  attach,
  combine,
  createEvent,
  createStore,
  sample,
  split,
} from 'effector'
import { createGate } from 'effector-react'
import { and, not, spread } from 'patronum'

import { Coordinates } from 'src/entities/geolocation'
import { $$navigate, NavigateParams } from 'src/entities/navigate'
import { remap } from 'src/shared/lib/effector/remap'
import { $$drugSearchConfigurationStorage } from 'src/shared/storage/drugSearch'

import { loadOptionsQuery } from '../api/drugConfigurationQuery'

import { $isDevice, $options } from './configuration'
import { filterSavedConfig, mapDefaultConfiguration } from './helpers'
import {
  ConfigChangedEvent,
  ConfigType,
  SavedConfig,
  ValidationErrors,
} from './types'

interface DrugSearchConfigGateGateProps {
  drugName: string
}

export const DrugSearchConfigGate = createGate<DrugSearchConfigGateGateProps>({
  name: 'DrugSearchConfigGate',
})

export const drugSearchConfigChanged = createEvent<ConfigChangedEvent>()
export const drugSearchConfigApplied = createEvent<void>()

export const preferredPharmacyChangeStarted = createEvent<Coordinates>()

const resetShape = split(drugSearchConfigChanged, {
  [ConfigType.Form]: ({ type }) => type === ConfigType.Form,
  [ConfigType.Dosage]: ({ type }) => type === ConfigType.Dosage,
  [ConfigType.TypeOfQuantity]: ({ type }) => type === ConfigType.TypeOfQuantity,
})

export const openNextScreenFx = attach({
  effect: $$navigate.navigateFx,
  source: remap(DrugSearchConfigGate.state, 'drugName'),
  mapParams({ to, state = {} }: NavigateParams, drugName) {
    return {
      to,
      state: { ...state, drugName },
    }
  },
})

const $daysSupply = createStore('').reset(DrugSearchConfigGate.close)
const $daysSupplyCustom = createStore('').reset(DrugSearchConfigGate.close)

export const $isCustomDaysSupplyValue = $daysSupply.map(
  (value) => value === 'custom',
)

const $form = createStore('').reset(DrugSearchConfigGate.close)
const $dosage = createStore('').reset(DrugSearchConfigGate.close)
const $typeOfQuantity = createStore('').reset(
  DrugSearchConfigGate.close,
  resetShape.form,
  resetShape.dosage,
)
const $quantity = createStore('').reset(DrugSearchConfigGate.close)

export const configFields = {
  $daysSupply,
  $daysSupplyCustom,
  $form,
  $dosage,
  $typeOfQuantity,
  $quantity,
}

export const $savedConfig = createStore<SavedConfig | null>(null).reset(
  DrugSearchConfigGate.close,
)

export const defaultValidationState: ValidationErrors = {
  [ConfigType.DaysSupply]: null,
  [ConfigType.DaysSupplyCustom]: null,
  [ConfigType.Form]: null,
  [ConfigType.Dosage]: null,
  [ConfigType.TypeOfQuantity]: null,
  [ConfigType.Quantity]: null,
}

const $defaultConfig = $options.map(
  (options) => options.find((drug) => drug.isDefault) ?? null,
)

const $ndc = combine(
  $typeOfQuantity,
  $dosage,
  $options,
  (typeOfQuantity, dosage, drugs) =>
    drugs.find(
      (drug) => drug.package === typeOfQuantity && drug.dosage === dosage,
    )?.ndc ?? '',
)

const $gpi = combine(
  $typeOfQuantity,
  $dosage,
  $options,
  (typeOfQuantity, dosage, drugs) =>
    drugs.find(
      (drug) => drug.package === typeOfQuantity && drug.dosage === dosage,
    )?.gpi ?? '',
)

export const $currentConfigState = combine({
  daysSupply: $daysSupply,
  daysSupplyCustom: $daysSupplyCustom,
  form: $form,
  dosage: $dosage,
  quantity: $quantity,
  typeOfQuantity: $typeOfQuantity,
  ndc: $ndc,
  gpi: $gpi,
})

export const $isFormFieldAvailable = combine(
  $currentConfigState,
  ({ daysSupply, daysSupplyCustom }) =>
    Boolean(daysSupplyCustom || (daysSupply && daysSupply !== 'custom')),
)

export const $drugFormOptions = $options.map((configs) =>
  configs.reduce((acc, { form }) => ({ ...acc, [form]: form }), {}),
)

export const $drugDosageOptions = combine(
  $form,
  $options,
  $isDevice,
  (currentForm, configOptions, isDevice) => {
    return configOptions
      .filter((config) =>
        isDevice && !currentForm
          ? config.form === configOptions[0].form
          : config.form === currentForm,
      )
      .reduce((acc, { dosage }) => ({ ...acc, [dosage]: dosage }), {})
  },
)

export const $drugQuantityTypeOptions = combine(
  $form,
  $dosage,
  $options,
  (currentForm, currentDosage, configOptions) =>
    configOptions
      .filter(
        (config) =>
          config.form === currentForm && config.dosage === currentDosage,
      )
      .reduce((acc, item) => ({ ...acc, [item.package]: item.package }), {}),
)

const configurationFormStores = {
  [ConfigType.DaysSupply]: $daysSupply,
  [ConfigType.DaysSupplyCustom]: $daysSupplyCustom,
  [ConfigType.Form]: $form,
  [ConfigType.Dosage]: $dosage,
  [ConfigType.TypeOfQuantity]: $typeOfQuantity,
  [ConfigType.Quantity]: $quantity,
}

spread({
  source: drugSearchConfigChanged.map(({ type, value }) => ({ [type]: value })),
  targets: configurationFormStores,
})

spread({
  source: sample({
    clock: loadOptionsQuery.finished.success,
    source: {
      savedConfig: $savedConfig,
      options: $options,
    },
    filter: ({ savedConfig }) => Boolean(savedConfig),
    fn: ({ savedConfig, options }) => filterSavedConfig(savedConfig!, options),
  }),
  targets: configurationFormStores,
})

sample({
  source: DrugSearchConfigGate.open,
  fn: ({ drugName }) => ({ name: drugName }),
  target: loadOptionsQuery.refresh,
})

sample({
  clock: sample({
    clock: DrugSearchConfigGate.open,
    source: $$drugSearchConfigurationStorage.$state,
    fn: (savedDrugs, { drugName }) =>
      savedDrugs.find((drug) => drug.drugName === drugName)?.config,
  }),
  filter: Boolean,
  target: $savedConfig,
})

sample({
  clock: drugSearchConfigChanged,
  filter: ({ type }) => type === ConfigType.DaysSupply,
  fn: ({ value }) => (value === 'custom' ? '' : value),
  target: $daysSupplyCustom,
})

// For a device-type drug, we set the first available dosage of the first available form by default.
sample({
  clock: $options,
  filter: $isDevice,
  fn: (options) => options[0].dosage,
  target: $dosage,
})

// After changing the $form, the available dosage options change accordingly,
// and for a device-type drug, the dose should be the first available.
sample({
  clock: $form,
  source: $drugDosageOptions,
  filter: $isDevice,
  fn: (dosages) => Object.values(dosages)[0] as string,
  target: $dosage,
})

sample({
  clock: resetShape.form,
  source: not($isDevice),
  filter: Boolean,
  target: $dosage.reinit,
})

spread({
  source: sample({
    clock: loadOptionsQuery.finished.success,
    source: $defaultConfig,
    filter: and($defaultConfig, not($savedConfig)),
    fn: (defaultConfig) => mapDefaultConfiguration(defaultConfig!),
  }),
  targets: configurationFormStores,
})
